// Mile Donations — finance/admin view over the NGO donation ledger (wave 3,
// report look: three all-time tiles, a filter card, then the modern table).
//
// Summary tiles come from the tiny server-only /ngo_stats tallies
// (all-time, atomic per donation); the table reads the /ngo_donations
// ledger date-bounded (indexed on "date") so the default view never pulls
// the full history. Each row shows the miles↔ETB correspondence recorded
// at donation time (rate snapshot included). The same reads as before:
// fetchNgos() once, then fetchNgoStats() + fetchNgoDonations(from) for the
// chosen period; read-only.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { api } from 'common';
import DashboardCard from '../components/DashboardCard';
import ClickableUserLink from '../components/ClickableUserLink';
import { EmptyState } from '../components/feedback';
import { CopyButton, DataTable, ChipGroup, DropChip, ActiveFilters, ALL, formatMoney, formatNumber, phoneMatches } from '../components/modern';

const PERIODS = [
  { key: '30', days: 30 },
  { key: '90', days: 90 },
  { key: '365', days: 365 },
  // 'All time' = rolling 730-day floor; revisit ~2028-08 (feature shipped 2026-08-23).
  { key: 'all', days: 730 },
];

export default function NgoDonations() {
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const ngos = useSelector((state) => state.ngodata.ngos);
  const users = useSelector((state) => state.usersdata?.users);
  const role = auth?.profile?.usertype;
  const permitted = role === 'admin' || role === 'finance';
  const symbol = settings?.symbol || 'ETB';
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);

  const [stats, setStats] = useState(null); // /ngo_stats keyed by ngoId
  const [rows, setRows] = useState(null); // ledger rows for the period
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('90');
  const [ngoFilter, setNgoFilter] = useState(ALL);
  const dispatch = useDispatch();

  // NGO names come from the /ngos slice (no longer pre-fetched at login).
  useEffect(() => {
    if (permitted) dispatch(api.fetchNgos());
  }, [dispatch, permitted]);

  const ngoName = useCallback((ngoId) => (ngos || []).find((n) => n.id === ngoId)?.name || ngoId, [ngos]);

  // New donations carry donorName/donorMobile denormalized by the server;
  // older rows fall back to a users-slice join, then the bare uid.
  const donorInfo = useCallback(
    (row) => {
      if (row.donorName || row.donorMobile) {
        return { name: row.donorName || row.uid, mobile: row.donorMobile || '' };
      }
      const u = (users || []).find((x) => x.id === row.uid);
      return {
        name: u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || row.uid : row.uid,
        mobile: u?.mobile || '',
      };
    },
    [users]
  );

  const load = useCallback(async (periodKey) => {
    setLoading(true);
    try {
      const p = PERIODS.find((x) => x.key === periodKey) || PERIODS[1];
      const fromTs = p.days ? Date.now() - p.days * 86400000 : null;
      const [statsData, ledger] = await Promise.all([api.fetchNgoStats(), api.fetchNgoDonations(fromTs)]);
      setStats(statsData);
      setRows(ledger);
    } catch (_) {
      setStats({});
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (permitted) load(period);
  }, [permitted, period, load]);

  if (!permitted) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState
          icon={<VolunteerActivismOutlinedIcon />}
          title={t('admin_finance_access_required', { defaultValue: 'Admin or finance access required.' })}
        />
      </Paper>
    );
  }

  // All-time totals across every NGO (from the atomic tallies).
  const totals = Object.values(stats || {}).reduce(
    (acc, s) => ({
      miles: acc.miles + (parseFloat(s.miles) || 0),
      etb: acc.etb + (parseFloat(s.etbValue) || 0),
      count: acc.count + (parseInt(s.donationCount, 10) || 0),
    }),
    { miles: 0, etb: 0, count: 0 }
  );

  const filteredRows = (rows || []).filter((r) => ngoFilter === ALL || r.ngoId === ngoFilter);
  // Period totals for the rows actually on screen (respects the NGO filter).
  const periodTotals = filteredRows.reduce(
    (acc, r) => ({
      miles: acc.miles + (parseFloat(r.miles) || 0),
      etb: acc.etb + (parseFloat(r.etbValue) || 0),
    }),
    { miles: 0, etb: 0 }
  );
  const round2 = (n) => Math.round(n * 100) / 100;

  const periodLabels = {
    30: t('last_30_days', { defaultValue: 'Last 30 days' }),
    90: t('last_90_days', { defaultValue: 'Last 90 days' }),
    365: t('last_year', { defaultValue: 'Last year' }),
    all: t('all_time', { defaultValue: 'All time' }),
  };
  const ngoOptions = [{ value: ALL, label: t('all', { defaultValue: 'All' }) }].concat(
    (ngos || []).map((n) => ({ value: n.id, label: n.name, count: (rows || []).filter((r) => r.ngoId === n.id).length }))
  );
  const hiddenText = t('hidden_demo', { defaultValue: 'Hidden for Demo' });
  const mobileOf = (row) => (allowPrivate ? donorInfo(row).mobile : donorInfo(row).mobile ? hiddenText : '');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });

  const columns = [
    { title: t('requestDate', { defaultValue: 'Date' }), field: 'date', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('ngo', { defaultValue: 'NGO' }),
      field: 'ngoId',
      render: (row) => ngoName(row.ngoId),
      sortValue: (row) => String(ngoName(row.ngoId)),
      searchValue: (row) => String(ngoName(row.ngoId)),
      exportValue: (row) => ngoName(row.ngoId),
    },
    {
      title: t('donor', { defaultValue: 'Donor' }),
      field: 'donorName',
      render: (row) => (row.uid ? <ClickableUserLink value={donorInfo(row).name} type="customer" userId={row.uid} /> : null),
      sortValue: (row) => String(donorInfo(row).name),
      searchValue: (row) => String(donorInfo(row).name),
      exportValue: (row) => donorInfo(row).name,
    },
    {
      title: t('phone', { defaultValue: 'Phone' }),
      field: 'donorMobile',
      nowrap: true,
      render: (row) => {
        const mobile = donorInfo(row).mobile;
        if (!mobile) return null;
        if (!allowPrivate) return hiddenText;
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <ClickableUserLink value={mobile} type="customer" userId={row.uid} iconType="phone" />
            <CopyButton value={mobile} phone what={phoneWord} sx={{ my: -0.5 }} />
          </Box>
        );
      },
      sortable: false,
      searchable: false,
      exportValue: mobileOf,
    },
    { title: t('miles', { defaultValue: 'Miles' }), field: 'miles', type: 'number' },
    {
      title: `${t('rate', { defaultValue: 'Rate' })} (${symbol}/${t('mile', { defaultValue: 'mile' })})`,
      field: 'rate',
      type: 'number',
    },
    { title: `${t('donation_value', { defaultValue: 'Value' })} (${symbol})`, field: 'etbValue', type: 'money' },
  ];

  const tile = (icon, hue, title, value) => (
    <DashboardCard icon={icon} hue={hue} title={title} value={value} loading={stats === null} />
  );
  const allTime = t('all_time', { defaultValue: 'all time' });
  const tokens =
    ngoFilter !== ALL ? [{ key: 'ngo', group: t('ngo', { defaultValue: 'NGO' }), value: ngoName(ngoFilter) }] : [];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* All-time summary from /ngo_stats */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
        {tile(
          <PaymentsOutlinedIcon />,
          'success',
          `${t('total_donated_value', { defaultValue: 'Total donated value' })} (${symbol}) · ${allTime}`,
          formatMoney(round2(totals.etb), settings)
        )}
        {tile(<StarsOutlinedIcon />, 'amber', `${t('total_miles_donated', { defaultValue: 'Total miles donated' })} · ${allTime}`, formatNumber(round2(totals.miles)))}
        {tile(<ReceiptLongOutlinedIcon />, 'info', `${t('donation_count', { defaultValue: 'Donations' })} · ${allTime}`, formatNumber(totals.count))}
      </Box>

      {/* Period + NGO filters */}
      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <ChipGroup
          label={t('period', { defaultValue: 'Period' })}
          value={period}
          onChange={setPeriod}
          options={PERIODS.map((p) => ({ value: p.key, label: periodLabels[p.key] }))}
        />
        <DropChip label={t('ngo', { defaultValue: 'NGO' })} value={ngoFilter} onChange={setNgoFilter} options={ngoOptions} />
      </Paper>

      <DataTable
        title={t('mile_donations_title', { defaultValue: 'Mile Donations' })}
        rows={filteredRows}
        columns={columns}
        rowKey={(row, index) => row.id || `${row.uid}-${row.date}-${index}`}
        loading={loading && rows === null}
        refreshing={loading && rows !== null}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('ngo_donations_count', {
                  count: total,
                  defaultValue_one: '{{count}} donation',
                  defaultValue_other: '{{count}} donations',
                  defaultValue: '{{count}} donations',
                })
        }
        subtitle={`${periodLabels[period]} · ${formatNumber(round2(periodTotals.miles))} ${t('miles', { defaultValue: 'miles' })} → ${formatMoney(
          round2(periodTotals.etb),
          settings
        )} ${symbol}`}
        headerContent={
          tokens.length ? <ActiveFilters tokens={tokens} onRemove={() => setNgoFilter(ALL)} onClearAll={() => setNgoFilter(ALL)} /> : null
        }
        searchPlaceholder={t('ngo_donations_search', { defaultValue: 'Search NGO or donor' })}
        searchFn={(row, q, ql) =>
          String(ngoName(row.ngoId)).toLowerCase().includes(ql) ||
          String(donorInfo(row).name).toLowerCase().includes(ql) ||
          (allowPrivate && phoneMatches(donorInfo(row).mobile, q))
        }
        rowLabel={(row) => donorInfo(row).name}
        prefsKey="ngo_donations"
        exportCsv
        exportPrint
        exportFileName="mile_donations"
        exportTitle={t('mile_donations_title', { defaultValue: 'Mile Donations' })}
        minWidth={920}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <VolunteerActivismOutlinedIcon />,
                title:
                  ngoFilter !== ALL
                    ? t('ngo_donations_empty_ngo', { period: periodLabels[period], defaultValue: 'No donations to this NGO in this period ({{period}})' })
                    : t('ngo_donations_empty', { period: periodLabels[period], defaultValue: 'No donations in this period ({{period}})' }),
                actionLabel: ngoFilter !== ALL ? t('clear_filters', { defaultValue: 'Clear filters' }) : undefined,
                onAction: ngoFilter !== ALL ? () => setNgoFilter(ALL) : undefined,
              }
        }
      />
    </Box>
  );
}
